import { Company } from '../../../types/commons';
import { UserRole } from '../../../types/user-role';

/**
 * Pure rules for what the signed-in user may do on the User Management page.
 * No Angular imports on purpose: the component wraps these in a `computed()` /
 * template calls, and the specs exercise them directly.
 *
 * These only decide what the UI OFFERS. The backend enforces the same rules and
 * refuses anything else with an error code, so a stale screen cannot escalate.
 */

/** Who is looking at the page. `userId` is the caller's own profile id. */
export interface CallerContext {
  role: UserRole | null;
  userId: string | null;
}

/** One listed user, reduced to what the permission rules need. */
export interface RowContext {
  id: string;
  role: UserRole | null;
}

/** The rows a manager may act on at all: Users and Admins, never a SuperAdmin or an unknown role. */
function isManageableRole(role: UserRole | null): boolean {
  return role === UserRole.User || role === UserRole.Admin;
}

/**
 * A SuperAdmin or an Admin may delete Users and Admins (an Admin only sees their
 * own company, so that means fellow Admins: P.O., 2026-10-20). Nobody may delete
 * their own account from this page: it would end the session the page is
 * running in, and the server refuses it with 403 `CANNOT_DELETE_SELF`.
 */
export function canDeleteRow(caller: CallerContext, row: RowContext): boolean {
  if (caller.userId !== null && row.id === caller.userId) {
    return false;
  }
  return canManageUsers(caller) && isManageableRole(row.role);
}

/**
 * A SuperAdmin or an Admin may edit Users and Admins, including a fellow Admin
 * and their own row (P.O., 2026-10-20). What an Admin may change inside the
 * form is narrower: Role per `canChangeRowRole`, Company never
 * (`canChangeCompany`). A plain User edits nobody.
 */
export function canEditRow(caller: CallerContext, row: RowContext): boolean {
  return canManageUsers(caller) && isManageableRole(row.role);
}

function canManageUsers(caller: CallerContext): boolean {
  return caller.role === UserRole.Admin || caller.role === UserRole.SuperAdmin;
}

/**
 * May the caller change THIS row's Role in the Edit User form? The picker then
 * offers exactly User and Admin (never SuperAdmin).
 * - A SuperAdmin: any User or Admin row, in any company.
 * - An Admin: any User or Admin row they can see, which is their own company
 *   only (the listing is company-scoped): downgrade an Admin (themselves
 *   included) to User, or promote a User to Admin (P.O., 2026-10-20,
 *   AS-1387 / AS-1388).
 * Company is a separate, stricter rule (`canChangeCompany`). The backend
 * enforces the same rule; until it allows the Admin cases it answers 403
 * `ROLE_CHANGE_SUPERADMIN_ONLY`, shown as an ordinary failure toast.
 */
export function canChangeRowRole(caller: CallerContext, row: RowContext): boolean {
  return canManageUsers(caller) && isManageableRole(row.role);
}

/**
 * May the caller change THIS row's Email in the Edit User form? (P.O., 2026-10-27)
 * - Never on their own row, whatever their role.
 * - A SuperAdmin: any Admin or User row, in any company.
 * - An Admin: a User row only (the listing is company-scoped, so that is a User
 *   of their own company). A fellow Admin's email is read-only.
 * The backend refuses anything else with 403 `EMAIL_CHANGE_NOT_PERMITTED`.
 */
export function canEditEmail(caller: CallerContext, row: RowContext): boolean {
  if (caller.userId !== null && row.id === caller.userId) {
    return false;
  }
  if (caller.role === UserRole.SuperAdmin) {
    return isManageableRole(row.role);
  }
  return caller.role === UserRole.Admin && row.role === UserRole.User;
}

/**
 * Only a SuperAdmin moves a user to another company. An Admin can neither move
 * themselves nor anyone else (403 `COMPANY_CHANGE_SUPERADMIN_ONLY`), and creates
 * users in their own company only, with the picker preset and locked.
 */
export function canChangeCompany(caller: CallerContext): boolean {
  return caller.role === UserRole.SuperAdmin;
}

/** Admin (own company) and SuperAdmin (any company) may add users. */
export function canCreateUsers(caller: CallerContext): boolean {
  return canManageUsers(caller);
}

/**
 * Companies the create/edit form's picker should offer. An Admin only sees their
 * own, matched by NAME because the profile exposes the company name, not its id.
 * An empty or unmatched name yields `[]` (a SuperAdmin's profile has company '').
 */
export function companyOptionsFor(
  caller: CallerContext,
  companies: Company[],
  ownCompanyName: string,
): Company[] {
  switch (caller.role) {
    case UserRole.SuperAdmin:
      return companies;
    case UserRole.Admin:
      return companies.filter((company) => company.name === ownCompanyName);
    default:
      return [];
  }
}

/** A filter with a single choice is noise: show it only when there is a real choice. */
export function isCompanyFilterVisible(companies: Company[]): boolean {
  return companies.length > 1;
}
